import { CaseVersionTimelineSummary } from "@acre/graphql"

export const getPathnameSuffix = (pathname: string) => {
  const contentBetweenSlashes = /[^/]+(?=\/$)/
  const match = pathname.match(contentBetweenSlashes)

  if (match) {
    return match[0]
  } else {
    const contentAfterFinalSlash = /[^/]+$/
    const matchAfterFinalSlash = pathname.match(contentAfterFinalSlash)

    return matchAfterFinalSlash ? matchAfterFinalSlash[0] : null
  }
}

/**
 * Calculates the total number of version jumps from the beginning of the timeline up to a specified index.
 *
 * @param {number} index - The index up to which the version jumps are summed.
 * @param {CaseVersionTimelineSummary[]} versionsTimeline - Full timeline list.
 * @returns {number} - The total version difference up to the specified index, plus 1 to account for the initial version difference.
 */
const getTotalVersionJumps = (index: number, versionsTimeline: CaseVersionTimelineSummary[]): number => {
  let sum = 0;

  if (versionsTimeline?.length) {
    for (let i = 0; i <= index; i++) {
      sum += versionsTimeline[i].version_to - versionsTimeline[i].version_from;
    }
  }

  return sum + 1;
};

/**
 * Determines if a timeline step is the active step based on the current version.
 *
 * @param index - The index of the step in the versionsTimeline array.
 * @param version - The current version selected (as a string from the URL).
 * @param {CaseVersionTimelineSummary[]} versionsTimeline - Full timeline list.
 * @returns boolean - True if the current step is the active one.
 */
export const checkIsActiveIndex = (index: number, version: string, versionsTimeline: CaseVersionTimelineSummary[]) => {
  // The timeline may skip versions (e.g., version_from: 1 → version_to: 3).
  // The version number in the URL may not match the index in the array.
  // Subtract those jumps from the current version to properly match the index.
  const jumps = getTotalVersionJumps(index, versionsTimeline);
  return index === Number(version) - jumps;
};

/**
 * Returns the step index (from the end) for Matomo tracking when a user clicks
 * a version in the case history. Adjusts for skipped/jumped versions.
 *
 * Example:
 *   "caseHistoryClick-2-Review" → User clicked the 3rd most recent visible step.
 *
 * @param {CaseVersionTimelineSummary} step - The clicked timeline step.
 * @param index - Index of the step in the timeline array.
 * @param {CaseVersionTimelineSummary[]} versionsTimeline - Full timeline list.
 * @returns number - Adjusted index from the end for tracking.
 */
export const getCaseHistoryClickIndex = (
  step: CaseVersionTimelineSummary,
  index: number,
  versionsTimeline: CaseVersionTimelineSummary[]
): number => {
  // The timeline may skip versions (e.g., version_from: 1 → version_to: 3).
  // `step.version_to` is the actual version, which may not match its index.
  // Subtract skipped versions to align with the visible timeline.
  const jumps = getTotalVersionJumps(index, versionsTimeline);
  const normalizedStep = step.version_to - jumps;

  // Get index from the end, adding 1 due to 1-based timeline versioning vs 0-based array indexing.
  return versionsTimeline.length - (normalizedStep + 1);
};